import {
  IonButtons,
  IonContent,
  IonHeader,
  IonModal,
  IonSpinner,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import { useTranslation } from 'react-i18next';
import { useSnoozedChefs, useUnsnoozeChef } from '../../hooks/useNotificationMutes';

interface SnoozedChefsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// Stable reference so Ionic doesn't re-init the sheet gesture mid-animation
// (a fresh array each render snaps the sheet closed — see ConnectionsListModal).
const SHEET_BREAKPOINTS = [0, 0.6, 0.9];

/**
 * Management sheet for the caller's per-chef new-dish snoozes: lists each
 * snoozed chef with an un-snooze action. Opened from the settings "N snoozed
 * chefs" row. Data is TanStack Query-backed (useSnoozedChefs); un-snooze
 * invalidates the list so rows drop out immediately.
 */
export const SnoozedChefsModal: React.FC<SnoozedChefsModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation('profile');
  // Defer the fetch until the sheet is actually open.
  const { data: mutes, isLoading } = useSnoozedChefs({ enabled: isOpen });
  const unsnooze = useUnsnoozeChef();

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={onClose}
      breakpoints={SHEET_BREAKPOINTS}
      initialBreakpoint={0.6}
      handle
      expandToScroll={false}
    >
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <button
              type="button"
              onClick={onClose}
              className="px-4 text-sm font-medium text-brand-primary"
            >
              {t('common:actions.done', { defaultValue: 'Done' })}
            </button>
          </IonButtons>
          <IonTitle
            className="ion-text-center"
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: 0,
              bottom: 0,
              pointerEvents: 'none',
            }}
          >
            {t('newDishSnooze.manageTitle')}
          </IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <div className="p-6">
          {isLoading ? (
            <div className="flex justify-center py-6">
              <IonSpinner name="crescent" className="h-5 w-5" />
            </div>
          ) : !mutes || mutes.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t('newDishSnooze.manageEmpty')}
            </p>
          ) : (
            <ul className="divide-y divide-border">
              {mutes.map((mute) => {
                const chef = mute.mutedProfile;
                const name = chef?.displayName?.trim() || t('newDishSnooze.chefFallback');
                return (
                  <li
                    key={mute.id}
                    className="flex items-center justify-between gap-3 py-3"
                  >
                    <div className="flex min-w-0 items-center gap-3">
                      {chef?.avatarUrl ? (
                        <img
                          src={chef.avatarUrl}
                          alt=""
                          className="h-9 w-9 flex-shrink-0 rounded-full object-cover"
                        />
                      ) : (
                        <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-muted text-xs font-medium text-muted-foreground">
                          {chef?.initials || name.charAt(0).toUpperCase()}
                        </span>
                      )}
                      <span className="truncate text-sm font-medium text-foreground">
                        {name}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => unsnooze.mutate(mute.mutedProfileId)}
                      disabled={unsnooze.isPending}
                      className="flex-shrink-0 rounded-lg btn-outline border-brand-primary px-3 py-1.5 text-xs font-semibold text-brand-primary active:opacity-80 disabled:opacity-50"
                    >
                      {t('newDishSnooze.unsnooze')}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </IonContent>
    </IonModal>
  );
};
